import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { StorageKeys } from 'utils/local-storage';

import { TabbedHeaderSkeleton } from './tabbed-header.skeleton';
import { TabLink, Wrapper } from './tabbed-header.styled';
import { ITabbedHeaderProps } from './types';

const TabbedHeader: FC<ITabbedHeaderProps> = ({ activeTab: newActiveTab, data, loading }) => {
  const [activeTab, setActiveTab] = useState<number | undefined>(-1);

  const mParticle = useMParticleContext();

  const tabbedHeaderRef = useRef<HTMLDivElement>(null);

  const logTabSelected = (entry: string, tabId: string): void => {
    mParticle.logEvent(CustomEventNames.TABBED_HEADER, EventTypes.Navigation, {
      'Selected Tab': entry,
    });
    mParticle.logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.PRODUCT_SECTION_TABS,
        text: entry,
        componentId: tabId,
      },
    });
  };

  const [prevActiveTab, setPrevActiveTab] = useLocalStorageState<number | null>({
    key: StorageKeys.TABBED_MENU_PREVIOUS_ACTIVE_TAB,
    defaultReturnValue: null,
  });

  useEffect(() => {
    if (prevActiveTab && typeof newActiveTab !== 'undefined' && newActiveTab < 0) {
      setActiveTab(prevActiveTab);
    } else {
      setActiveTab(newActiveTab);
    }

    if (typeof newActiveTab !== 'undefined' && newActiveTab >= 0) {
      setPrevActiveTab(newActiveTab);
    }
  }, [prevActiveTab, newActiveTab, setPrevActiveTab]);

  useLayoutEffect(() => {
    if (tabbedHeaderRef?.current && activeTab) {
      const parent = tabbedHeaderRef.current;
      const element = tabbedHeaderRef.current.children[activeTab];
      if (element instanceof HTMLElement) {
        const { left, right } = element.getBoundingClientRect();
        const containerWidth = parent.offsetWidth;

        if (left < 1 || left > containerWidth || right > containerWidth) {
          // Old javascript contexts do not support `scrollTo`
          parent.scrollTo?.({ left: element.offsetLeft, behavior: 'smooth' });
        }
      }
    }
  }, [activeTab, tabbedHeaderRef]);

  if (loading) {
    return <TabbedHeaderSkeleton />;
  }

  return (
    <Wrapper data-testid="tabbed-menu-header" ref={tabbedHeaderRef}>
      {data.map(({ label, to, tabId }, i) => (
        <TabLink
          key={label}
          to={to}
          active={i === activeTab}
          onClick={() => logTabSelected(label, tabId)}
        >
          {label}
        </TabLink>
      ))}
    </Wrapper>
  );
};

export default TabbedHeader;
